import React, {useMemo} from 'react';
import {useSelector, useStore} from 'react-redux';
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectFinalRound,
  selectHexes,
  selectIsInitiativeInterceptedWithIsolationism,
  selectNextInitiativePlayerId,
  selectPhase,
  selectRound,
  selectSpecialBuildings,
  selectUnits
} from '../../../redux/reducers/sharedStateReducer/selectors';
import {selectHeaderOwnPlayerView} from '../../../utils/gameState/user/selectHeaderOwnPlayerView';
import {buildPlayerStatsView, CONSTRUCTION_ACTIONS_MODES} from '../../../utils/players/stats/buildPlayerStatsView';
import {pluralizeRu} from '../../../utils/other/pluralizeRu';
import {getPlayerSurfaceColor} from '../../../utils/players/getPlayerHexColor';
import {EngravedIcon} from '../../gameUi/EngravedIcon';
import {joinClassNames} from '../../gameUi/joinClassNames';
import FactionSkinIcon from '../../RankSkinFrame/FactionSkinIcon';
import UnitIcon from '../../TokensToolbar/NewUnitToolbar/UnitIcon/UnitIcon';
import HeaderDropdown, {HEADER_DROPDOWN_ANCHORS} from '../HeaderDropdown/HeaderDropdown';
import './PlayerStatsDropdown.css';

// Цвет чисел ресурса — как у монет и очков во всем интерфейсе
const RESOURCE_TEXT_CLASSES = {
  coins: 'text-coins',
  winPoints: 'text-win-points',
};

const formatSigned = (amount) => (amount >= 0 ? `+${amount}` : `${amount}`);

/** Блок панели: заголовок (с иконкой, если она есть) и итог справа, под ним строки */
const StatsSection = ({icon, isMaskIcon = false, title, summary, children}) => (
  <section className={'player-stats_section'}>
    <div className={'player-stats_section-header'}>
      {icon && (isMaskIcon
        ? <EngravedIcon src={icon} size={20} className={'player-stats_section-icon'}/>
        : <img className={'player-stats_section-icon'} src={icon} alt={''}/>)}
      <h3 className={'player-stats_section-title'}>{title}</h3>
      {summary && <span className={'player-stats_section-summary'}>{summary}</span>}
    </div>
    <div className={'player-stats_rows'}>
      {children}
    </div>
  </section>
);

/**
 * Строка: значок (жетон, иконка отряда), подпись и пояснение под ней слева,
 * число справа — цветом своего ресурса (amountClassName)
 */
const StatsRow = ({icon, label, note, amount, amountClassName, isMuted = false, isTotal = false}) => (
  <div className={joinClassNames(
    'player-stats_row',
    isMuted && 'player-stats_row_muted',
    isTotal && 'player-stats_row_total',
  )}>
    {icon}
    <div className={'player-stats_row-text'}>
      <span className={'player-stats_row-label'}>{label}</span>
      {note && <span className={'player-stats_row-note'}>{note}</span>}
    </div>
    {amount !== undefined && amount !== null && (
      <span className={joinClassNames('player-stats_row-amount', amountClassName)}>{amount}</span>
    )}
  </div>
);

/** Итог блока ресурса: «12 +4 за раунд», числа — цветом ресурса */
const ResourceSummary = ({current, income, resourceClassName}) => (
  <>
    <span className={resourceClassName}>{current}</span>
    {' '}
    <span className={resourceClassName}>{formatSigned(income)}</span>
    {' за раунд'}
  </>
);

/**
 * Группа источников дохода: жетон, «Гильдии ×2 по +3» (доход — цветом
 * ресурса) и из чего складывается +3
 */
const IncomeGroupRow = ({group, resourceClassName}) => {
  const tokenIcon = group.image
    ? <img className={'player-stats_token'} src={group.image} alt={''}/>
    : null;
  const countText = group.count > 1 ? ` ×${group.count}` : '';
  if (group.isBlocked) {
    return (
      <StatsRow
        icon={tokenIcon}
        label={`${group.label}${countText}`}
        note={group.count > 1
          ? 'заблокированы противником или повреждены — дохода нет'
          : 'заблокировано противником или повреждено — дохода нет'}
        amount={'0'}
        amountClassName={resourceClassName}
        isMuted
      />
    );
  }
  const partsText = group.parts.length > 1
    ? group.parts.map((part) => `${part.amount} ${part.label}`).join(' + ')
    : null;
  return (
    <StatsRow
      icon={tokenIcon}
      label={(
        <>
          {`${group.label}${countText}`}
          {group.count > 1 && (
            <>
              {' по '}
              <span className={resourceClassName}>{formatSigned(group.amountEach)}</span>
            </>
          )}
        </>
      )}
      note={partsText}
      amount={formatSigned(group.total)}
      amountClassName={resourceClassName}
    />
  );
};

/**
 * Доплата к доходу (бонус, способность фракции): начисляется, только пока у
 * игрока есть хотя бы один город, — без городов строка приглушена. Бонус за
 * каждый город (perCity, см. calcPlayerIncomeBreakdown) подписан ставкой, а
 * вместе с фиксированной частью (fixed) — обеими
 */
const getIncomeAdditionNote = (addition) => {
  if (!addition.isPaid) {
    return 'без городов не начисляется';
  }
  if (!(addition.perCity > 0)) {
    return null;
  }
  return addition.fixed > 0
    ? `+${addition.fixed} и по +${addition.perCity} за каждый город`
    : `по +${addition.perCity} за каждый город`;
};

const IncomeAdditionRow = ({addition, resourceClassName}) => (
  <StatsRow
    label={addition.label}
    note={getIncomeAdditionNote(addition)}
    amount={formatSigned(addition.amount)}
    amountClassName={resourceClassName}
    isMuted={!addition.isPaid}
  />
);

const CoinsSection = ({coins}) => {
  const resourceClassName = RESOURCE_TEXT_CLASSES.coins;
  const isWithoutCities = coins.citiesCount === 0;
  return (
    <StatsSection
      icon={'/img/icons/coins.png'}
      title={'Монеты'}
      summary={<ResourceSummary current={coins.current} income={coins.income} resourceClassName={resourceClassName}/>}
    >
      <StatsRow
        label={'Базовый доход'}
        note={isWithoutCities
          ? 'нет ни одного города — базового дохода нет'
          : `${coins.citiesCount} ${pluralizeRu(coins.citiesCount, 'город', 'города', 'городов')}`}
        amount={formatSigned(coins.basic)}
        amountClassName={resourceClassName}
        isMuted={isWithoutCities}
      />
      {coins.additions.map((addition) => (
        <IncomeAdditionRow key={addition.id} addition={addition} resourceClassName={resourceClassName}/>
      ))}
      {coins.groups.map((group) => (
        <IncomeGroupRow key={group.key} group={group} resourceClassName={resourceClassName}/>
      ))}
      {coins.groups.length === 0 && (
        <StatsRow label={'Рудников и гильдий нет'} note={'монеты сверх базового дохода приносят рудники и гильдии'} isMuted/>
      )}
    </StatsSection>
  );
};

const WinPointsSection = ({winPoints, finalWinPoints, round}) => {
  const resourceClassName = RESOURCE_TEXT_CLASSES.winPoints;
  const {ruinsIncreasedStartRound} = winPoints;
  const isRuinsWindowAhead = ruinsIncreasedStartRound !== null && round < ruinsIncreasedStartRound;
  return (
    <StatsSection
      icon={'/img/icons/win points.png'}
      title={'Очки победы'}
      summary={<ResourceSummary current={winPoints.current} income={winPoints.income} resourceClassName={resourceClassName}/>}
    >
      {/* Очки к доходу от бонуса («божество») — как доплаты монетами выше */}
      {winPoints.additions.map((addition) => (
        <IncomeAdditionRow key={addition.id} addition={addition} resourceClassName={resourceClassName}/>
      ))}
      {winPoints.groups.map((group) => (
        <IncomeGroupRow key={group.key} group={group} resourceClassName={resourceClassName}/>
      ))}
      {winPoints.totalitarianism && (
        <StatsRow
          label={'Тоталитаризм'}
          note={`рабочих военных строений: ${winPoints.totalitarianism.militaryBuildingsCount}, `
            + `+1 за каждые ${winPoints.totalitarianism.ratio}`}
          amount={formatSigned(winPoints.totalitarianism.amount)}
          amountClassName={resourceClassName}
        />
      )}
      {winPoints.groups.length === 0 && !winPoints.totalitarianism && (
        <StatsRow
          label={'Руин и храмов нет'}
          // С очками от бонуса «приносят руины и храмы» спорило бы со строкой выше
          note={winPoints.additions.length > 0
            ? 'сверх бонуса очки за раунд приносят руины и храмы'
            : 'очки за раунд приносят руины и храмы'}
          isMuted
        />
      )}
      {isRuinsWindowAhead && (
        <StatsRow
          label={`С ${ruinsIncreasedStartRound}-го раунда руины приносят вдвое больше`}
          isMuted
        />
      )}
      <StatsRow
        label={'Итоговые очки, если партия закончится сейчас'}
        note={`очки + монеты по курсу ${finalWinPoints.coinsRate}:1`}
        amount={finalWinPoints.total}
        amountClassName={resourceClassName}
        isTotal
      />
    </StatsSection>
  );
};

const CONSTRUCTION_ACTIONS_TITLES = {
  [CONSTRUCTION_ACTIONS_MODES.CURRENT]: 'Действия строительства',
  [CONSTRUCTION_ACTIONS_MODES.THIS_ROUND]: 'Действия строительства в этом раунде',
  [CONSTRUCTION_ACTIONS_MODES.NEXT_ROUND]: 'Действия строительства в следующем раунде',
};

const ConstructionActionsSection = ({actions}) => {
  const isCurrent = actions.mode === CONSTRUCTION_ACTIONS_MODES.CURRENT;
  return (
    <StatsSection
      icon={'/img/icons/action.png'}
      isMaskIcon
      title={CONSTRUCTION_ACTIONS_TITLES[actions.mode]}
      summary={isCurrent ? `${actions.used} / ${actions.total}` : `${actions.total}`}
    >
      {actions.sources.map((source) => (
        <StatsRow key={source.id} label={source.label} amount={formatSigned(source.amount)}/>
      ))}
      {!isCurrent && (
        <StatsRow
          label={'Прогноз по текущему положению'}
          note={'число действий считается в начале фазы строительства: дворцы должны работать, политика — та, что будет действовать'}
          isMuted
        />
      )}
      {isCurrent && (
        <>
          <div className={'player-stats_subheader'}>
            {actions.isPlan ? 'Запланировано' : 'Потрачено'}
            {` — осталось ${actions.remaining}`}
          </div>
          {actions.spent.length === 0 && (
            <StatsRow label={'Пока ничего'} isMuted/>
          )}
          {actions.spent.map((item) => (
            <StatsRow
              key={item.key}
              label={item.label}
              note={item.isFree
                ? 'не тратит действие'
                : (item.price !== null ? `${item.price} ${pluralizeRu(item.price, 'монета', 'монеты', 'монет')}` : null)}
              amount={item.isFree ? '0' : '1'}
              isMuted={item.isFree}
            />
          ))}
        </>
      )}
    </StatsSection>
  );
};

/** Отряды по типам: иконка типа цветом игрока (с его образом отрядов), число и размеры */
const UnitsSection = ({units, player}) => (
  <StatsSection
    title={'Отряды'}
    summary={units.count > 0
      ? (
        <>
          {`${units.count} шт.`}
          <span className={'player-stats_summary-gap'}>{`Общий размер ${units.totalSize}`}</span>
        </>
      )
      : '0 шт.'}
  >
    {units.byType.map((type) => (
      <StatsRow
        key={type.typeId}
        icon={(
          <span className={'player-stats_unit-icon-cell'} title={`${type.label} ${type.dice}`}>
            <UnitIcon
              unitType={type.typeId}
              playerColor={player.color}
              factionId={player.factionId}
              hasUnitSkin={player.hasUnitSkin}
              className={'player-stats_unit-icon'}
            />
          </span>
        )}
        label={`${type.label}: ${type.count} шт.`}
        note={`размер: ${type.units.map((unit) => `${unit.size}/${unit.maxSize}`).join(', ')}`}
      />
    ))}
    {units.count === 0 && <StatsRow label={'Отрядов нет'} isMuted/>}
  </StatsSection>
);

/** Заголовок панели: портрет игрока и имя его цветом */
const PlayerTitle = ({player}) => (
  <span className={'player-stats_title'}>
    <FactionSkinIcon
      imgClassName={'player-stats_title-icon'}
      factionId={player.factionId}
      skinId={player.selectedSkin}
      alt={player.factionId}
    />
    <span className={'player-stats_title-name'} style={{color: getPlayerSurfaceColor(player.color)}}>
      {player.name}
    </span>
  </span>
);

/**
 * Панель статов игрока — выпадает от его плиты в шапке (как рынок
 * политик от своей кнопки; на телефоне — лист снизу): из чего складывается
 * доход монет и очков, действия строительства (в фазе строительства — на что
 * потрачены), отряды по типам и итоговые очки на сейчас. Чей игрок — тот же,
 * что в шапке (selectHeaderOwnPlayerView). Содержимое считается только у
 * открытой панели
 *
 * @param {boolean} isOpen
 * @param {function(): void} onClose
 * @param {boolean} [isFocused] - верхняя в стопке выпадающих панелей
 * @param {boolean} [isSpectatorMode]
 */
const PlayerStatsDropdown = ({isOpen, onClose, isFocused = false, isSpectatorMode = false}) => {
  const store = useStore();
  const view = useSelector((state) => selectHeaderOwnPlayerView(state, isSpectatorMode));
  // Пересчет — по частям состояния, из которых складываются статы
  const citiesCache = useSelector(selectCitiesCache);
  const units = useSelector(selectUnits);
  const hexes = useSelector(selectHexes);
  const specialBuildings = useSelector(selectSpecialBuildings);
  const buildingsDamage = useSelector(selectBuildingsDamage);
  const phase = useSelector(selectPhase);
  const round = useSelector(selectRound);
  const finalRound = useSelector(selectFinalRound);
  const nextInitiativePlayerId = useSelector(selectNextInitiativePlayerId);
  const isInterceptedWithIsolationism = useSelector(selectIsInitiativeInterceptedWithIsolationism);

  const stats = useMemo(() => (
    isOpen && view
      ? buildPlayerStatsView(store.getState(), view.player, view.pendingBuildings)
      : null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [
    isOpen, view, store, citiesCache, units, hexes, specialBuildings, buildingsDamage,
    phase, round, finalRound, nextInitiativePlayerId, isInterceptedWithIsolationism,
  ]);

  return (
    <HeaderDropdown
      isOpen={isOpen}
      onClose={onClose}
      anchor={HEADER_DROPDOWN_ANCHORS.STATS}
      isFocused={isFocused}
      title={view ? <PlayerTitle player={view.player}/> : 'Ресурсы'}
      isSheetOnPhone
      className={'player-stats'}
    >
      {stats && (
        <div className={'player-stats_sections'}>
          <CoinsSection coins={stats.coins}/>
          <WinPointsSection winPoints={stats.winPoints} finalWinPoints={stats.finalWinPoints} round={round}/>
          {stats.constructionActions && <ConstructionActionsSection actions={stats.constructionActions}/>}
          <UnitsSection units={stats.units} player={view.player}/>
        </div>
      )}
    </HeaderDropdown>
  );
};

export default PlayerStatsDropdown;
